/**
 * Atlas Web — App root.
 *
 * Renders the router. Unauthenticated visitors are routed to /login by
 * the RequireAuth guard; authenticated users land on /app/home.
 */
import { Route, Routes, Navigate } from "react-router-dom";
import { useAuthStore } from "@/lib/auth/store";
import { restoreSession } from "@/lib/auth/refresh";
import { AppShell } from "@/components/AppShell";
import { QueryError } from "@/components/QueryError";
import { Skeleton } from "@/components/ui";
import { WorkspaceUnavailable } from "@/components/WorkspaceUnavailable";
import { routeDefs } from "@/app/routes";

/**
 * The token lives in memory only (S2-05), so a reload starts with none and
 * asks the server, through the refresh cookie, whether a session exists
 * (restoreSession, lib/auth/refresh.ts). Until it answers, the screen waits
 * rather than sending someone who is signed in to /login. An expired token
 * is renewed rather than treated as signed out, so the browser's clock no
 * longer decides anything here.
 */
function RequireAuth({ children }: { children: React.ReactElement }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const sessionStatus = useAuthStore((s) => s.sessionStatus);
  const restoreError = useAuthStore((s) => s.restoreError);
  // Set centrally by the API client when the session gate answers 403
  // TENANT_SUSPENDED / TENANT_NOT_ACTIVE (lib/api/client.ts).
  const workspaceBlock = useAuthStore((s) => s.workspaceBlock);
  if (sessionStatus === "restoring") {
    return <SessionRestoring error={restoreError} />;
  }
  if (!accessToken) return <Navigate to="/login" replace />;
  if (workspaceBlock) return <WorkspaceUnavailable />;
  return children;
}

/**
 * While the session is being restored: a skeleton, or, when the server could
 * not be reached, its error with Try again. Never a skeleton that waits
 * forever.
 */
function SessionRestoring({ error }: { error: unknown }) {
  return (
    <main
      aria-busy={error ? undefined : true}
      style={{ padding: 24, maxInlineSize: 640, marginInline: "auto" }}
      data-testid="session-restoring"
    >
      {error ? (
        <QueryError error={error} onRetry={() => void restoreSession()} />
      ) : (
        <Skeleton rows={6} />
      )}
    </main>
  );
}

function RequireTenant({ children }: { children: React.ReactElement }) {
  const activeTenantCode = useAuthStore((s) => s.activeTenantCode);
  if (!activeTenantCode) return <Navigate to="/create-workspace" replace />;
  return children;
}

/**
 * A screen someone cannot use is not reachable.
 *
 * Until now no route carried a guard at all: a warehouse clerk could type
 * /app/finance/journal-entries and the page loaded. The API refused the data,
 * so nothing leaked — but they were shown a screen of errors and a product
 * that appeared broken rather than one that was not theirs.
 *
 * This is the second half of a pair. The API check is the one that protects
 * the data and it stays exactly where it is; this only stops offering a door
 * that will not open. A guard in the browser is a courtesy, never a control.
 */
function RequirePermission({
  permission,
  children,
}: {
  permission?: string;
  children: React.ReactElement;
}) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  if (permission && !hasPermission(permission)) {
    // Home rather than an error page: the person has not done anything wrong,
    // and a dead end is a worse answer than the screen they can use.
    return <Navigate to="/app/home" replace />;
  }
  return children;
}

export function App() {
  return (
    <Routes>
      {routeDefs.map((def) => {
        if (def.public) {
          return <Route key={def.path} path={def.path} element={def.element} />;
        }
        if (def.shell) {
          return (
            <Route
              key={def.path}
              path={def.path}
              element={
                <RequireAuth>
                  <RequireTenant>
                    <RequirePermission permission={def.permission}>
                      <AppShell>{def.element}</AppShell>
                    </RequirePermission>
                  </RequireTenant>
                </RequireAuth>
              }
            />
          );
        }
        return (
          <Route
            key={def.path}
            path={def.path}
            element={
              <RequireAuth>
                <RequireTenant>
                  <RequirePermission permission={def.permission}>
                    {def.element}
                  </RequirePermission>
                </RequireTenant>
              </RequireAuth>
            }
          />
        );
      })}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
